import React from 'react';
import { DollarSign, Heart, ShoppingCart, Users, Award } from 'lucide-react';
import { Translate } from 'react-redux-i18n';
import { FranchiseSnapshotModel, MetricKey } from '../types';
import { aggregateFranchiseMetrics, shouldShowSalesRow, shouldShowFundraisingRow, shouldShowPlaceHubRow, getFranchiseCurrency } from '../utils';
import CurrencyHelper from '../../../../helpers/currencyHelper';
import { MetricCard } from './MetricCard';
import { MetricsGrid } from './MetricsGrid';
import SystemOnlyBadge from '../../../../components/common/SystemOnlyBadge';

export interface FranchiseMetricsProps {
  franchise: FranchiseSnapshotModel;
  includeOffline: boolean;
  isSystemAdmin: boolean;
  showAllMetrics: boolean;
  isAnimating: boolean;
  selectedMetric: MetricKey | null;
  onMetricSelect: (metric: MetricKey) => void;
}

export const FranchiseMetrics: React.FC<FranchiseMetricsProps> = ({
  franchise,
  includeOffline,
  isSystemAdmin,
  showAllMetrics,
  isAnimating,
  selectedMetric,
  onMetricSelect
}) => {
  const metrics = aggregateFranchiseMetrics(franchise, includeOffline);
  const currency = getFranchiseCurrency(franchise);

  // Check what rows to show based on aggregated data
  const hasSales = metrics.saleCount > 0 || metrics.saleRaisedAmount > 0;
  const hasFundraising = metrics.pageCount > 0;
  const hasPlaceHub = metrics.ambassadorTotalCount > 0;

  // Calculate fee percentage for sysadmin display
  const totalRaised = includeOffline 
    ? metrics.raisedAmount 
    : (metrics.saleRaisedOnlineAmount + metrics.donationRaisedOnlineAmount);
  const feePercentage = totalRaised > 0 ? (metrics.feeCollected / totalRaised) * 100 : 0;

  // Helper function to split currency into major and minor parts for styling
  const splitCurrencyParts = (formatted: string): { major: string; minor: string } => {
    // Match pattern: currency symbol + amount + decimal separator + cents
    const match = formatted.match(/^(.*?)([\.,])(\d{2})$/);
    if (!match) {
      return { major: formatted, minor: '' };
    }
    const [, major, separator, cents] = match;
    return { major, minor: `${separator}${cents}` };
  };

  // Format fee value with percentage and de-emphasized decimals
  const formatFeeValue = (value: number) => {
    const currencyString = CurrencyHelper.formatCurrency(value, currency, { alwaysShowDecimals: true });
    const { major, minor } = splitCurrencyParts(currencyString);
    
    return (
      <>
        {major}
        {minor && <span className="franchise-metric-value-minor">{minor}</span>}
        <span style={{ marginLeft: '0.5rem', opacity: 0.7, fontSize: '0.9em' }}>
          ({feePercentage.toFixed(1)}%)
        </span>
      </>
    );
  };

  // Row 1 - Always visible (3 metrics)
  const row1Metrics = (
    <>
      <MetricCard
        label={<Translate value="franchise.hub.metrics.raised-amount" />}
        value={totalRaised}
        isCurrency
        currency={currency}
        isAnimating={isAnimating}
        icon={<DollarSign />}
        colorClass={selectedMetric === 'raisedAmount' ? 'franchise-metric-selected' : 'franchise-metric-clickable'}
        onClick={() => onMetricSelect('raisedAmount')}
      />

      <MetricCard
        label={
          includeOffline ? (
            <Translate value="franchise.hub.metrics.donation-count" />
          ) : (
            <Translate value="franchise.hub.metrics.donation-count-online" />
          )
        }
        value={includeOffline ? metrics.donationCount : metrics.donationOnlineCount}
        isAnimating={isAnimating}
        icon={<Heart />}
        colorClass={selectedMetric === 'donationCount' ? 'franchise-metric-selected' : 'franchise-metric-clickable'}
        onClick={() => onMetricSelect('donationCount')}
      />

      {isSystemAdmin ? (
        <MetricCard
          label={
            <>
              <Translate value="franchise.hub.metrics.fee-collected" />
              <SystemOnlyBadge />
            </>
          }
          value={metrics.feeCollected}
          formatValue={formatFeeValue}
          isAnimating={isAnimating}
          colorClass={selectedMetric === 'feeCollected' ? 'franchise-metric-selected' : 'franchise-metric-clickable'}
          onClick={() => onMetricSelect('feeCollected')}
        />
      ) : (
        <MetricCard
          label={
            includeOffline ? (
              <Translate value="franchise.hub.metrics.avg-donation" />
            ) : (
              <Translate value="franchise.hub.metrics.avg-donation-online" />
            )
          }
          value={includeOffline ? metrics.donationAverageAmount : metrics.donationAverageOnlineAmount}
          isCurrency
          currency={currency}
          isAnimating={isAnimating}
          colorClass={selectedMetric === 'donationAverageAmount' ? 'franchise-metric-selected' : 'franchise-metric-clickable'}
          onClick={() => onMetricSelect('donationAverageAmount')}
        />
      )}
    </>
  );

  // Row 2 - Sales (show if hasSales)
  const row2Metrics = hasSales && showAllMetrics && (
    <>
      <MetricCard
        label={
          includeOffline ? (
            <Translate value="franchise.hub.metrics.sale-amount" />
          ) : (
            <Translate value="franchise.hub.metrics.sale-amount-online" />
          )
        }
        value={includeOffline ? metrics.saleRaisedAmount : metrics.saleRaisedOnlineAmount}
        isCurrency
        currency={currency}
        isAnimating={isAnimating}
        icon={<ShoppingCart />}
        colorClass={selectedMetric === 'saleRaisedAmount' ? 'franchise-metric-selected' : 'franchise-metric-clickable'}
        onClick={() => onMetricSelect('saleRaisedAmount')}
      />

      <MetricCard
        label={
          includeOffline ? (
            <Translate value="franchise.hub.metrics.donation-amount" />
          ) : (
            <Translate value="franchise.hub.metrics.donation-amount-online" />
          )
        }
        value={includeOffline ? metrics.donationRaisedAmount : metrics.donationRaisedOnlineAmount}
        isCurrency
        currency={currency}
        isAnimating={isAnimating}
        colorClass={selectedMetric === 'donationRaisedAmount' ? 'franchise-metric-selected' : 'franchise-metric-clickable'}
        onClick={() => onMetricSelect('donationRaisedAmount')}
      />

      <MetricCard
        label={<Translate value="franchise.hub.metrics.ticket-count" />}
        value={metrics.saleDetailCount}
        isAnimating={isAnimating}
        colorClass={selectedMetric === 'saleDetailCount' ? 'franchise-metric-selected' : 'franchise-metric-clickable'}
        onClick={() => onMetricSelect('saleDetailCount')}
      />
    </>
  );

  // Row 3 - Fundraising (show if hasFundraising)
  const row3Metrics = hasFundraising && showAllMetrics && (
    <>
      <MetricCard
        label={<Translate value="franchise.hub.metrics.page-count" />}
        value={metrics.pageCount}
        isAnimating={isAnimating}
        icon={<Users />}
        colorClass={selectedMetric === 'pageCount' ? 'franchise-metric-selected' : 'franchise-metric-clickable'}
        onClick={() => onMetricSelect('pageCount')}
      />

      <MetricCard
        label={<Translate value="franchise.hub.metrics.pages-with-donations" />}
        value={metrics.pagesWithDonations}
        isAnimating={isAnimating}
        colorClass={selectedMetric === 'pagesWithDonations' ? 'franchise-metric-selected' : 'franchise-metric-clickable'}
        onClick={() => onMetricSelect('pagesWithDonations')}
      />

      <MetricCard
        label={<Translate value="franchise.hub.metrics.fundraiser-avg-raised" />}
        value={metrics.fundraiserAverageRaisedAmount}
        isCurrency
        currency={currency}
        isAnimating={isAnimating}
        colorClass={selectedMetric === 'fundraiserAverageRaisedAmount' ? 'franchise-metric-selected' : 'franchise-metric-clickable'}
        onClick={() => onMetricSelect('fundraiserAverageRaisedAmount')}
      />
    </>
  );

  // Row 4 - PlaceHub (show if hasPlaceHub)
  const row4Metrics = hasPlaceHub && showAllMetrics && (
    <>
      <MetricCard
        label={<Translate value="franchise.hub.metrics.ambassador-total" />}
        value={metrics.ambassadorTotalCount}
        isAnimating={isAnimating}
        icon={<Award />}
        colorClass={selectedMetric === 'ambassadorTotalCount' ? 'franchise-metric-selected' : 'franchise-metric-clickable'}
        onClick={() => onMetricSelect('ambassadorTotalCount')}
      />

      <MetricCard
        label={<Translate value="franchise.hub.metrics.ambassador-pages" />}
        value={metrics.ambassadorPageCount}
        isAnimating={isAnimating}
        colorClass={selectedMetric === 'ambassadorPageCount' ? 'franchise-metric-selected' : 'franchise-metric-clickable'}
        onClick={() => onMetricSelect('ambassadorPageCount')}
      />

      <MetricCard
        label={
          includeOffline ? (
            <Translate value="franchise.hub.metrics.ambassador-raised" />
          ) : (
            <Translate value="franchise.hub.metrics.ambassador-raised-online" />
          )
        }
        value={includeOffline ? metrics.ambassadorFundraisedAmount : metrics.ambassadorFundraisedOnlineAmount}
        isCurrency
        currency={currency}
        isAnimating={isAnimating}
        colorClass={selectedMetric === 'ambassadorFundraisedAmount' ? 'franchise-metric-selected' : 'franchise-metric-clickable'}
        onClick={() => onMetricSelect('ambassadorFundraisedAmount')}
      />
    </>
  );

  return (
    <div className="franchise-metrics">
      <h2 className="franchise-metrics-title">
        <Translate value="franchise.hub.metrics.title" />
      </h2>

      {/* Row 1 - Always visible */}
      <MetricsGrid columns={3}>
        {row1Metrics}
      </MetricsGrid>

      {/* Rows 2-4 - Show when showAllMetrics is true */}
      {showAllMetrics && (
        <>
          {row2Metrics && (
            <MetricsGrid columns={3}>
              {row2Metrics}
            </MetricsGrid>
          )}

          {row3Metrics && (
            <MetricsGrid columns={3}>
              {row3Metrics}
            </MetricsGrid>
          )}

          {row4Metrics && (
            <MetricsGrid columns={3}>
              {row4Metrics}
            </MetricsGrid>
          )}
        </>
      )}
    </div>
  );
};
